import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Layout from '../components/Layout';
import FuelPriceTable from '../components/FuelPriceTable';
import AdPlaceholder from '../components/AdPlaceholder';
import SEOContent from '../components/SEOContent';
import useTheme from '../hooks/useTheme';
import useExchangeRate from '../hooks/useExchangeRate';
import useFuelPrices from '../hooks/useFuelPrices';

const FuelPricePage = ({ pageConfig }) => {
  const { darkMode, toggleDarkMode } = useTheme();
  const { dollarRate } = useExchangeRate();

  const allCodes = useMemo(() => {
    const codes = [...pageConfig.regionCodes];
    if (pageConfig.nationalCode && !codes.includes(pageConfig.nationalCode)) {
      codes.push(pageConfig.nationalCode);
    }
    return codes;
  }, [pageConfig]);

  const { prices, isLoading, lastUpdated } = useFuelPrices(allCodes);

  const nationalAverage = useMemo(() => {
    if (pageConfig.isNational) return null;
    const nat = prices.find(p => p.regionCode === (pageConfig.nationalCode || 'NUS'));
    return nat ? nat.price : null;
  }, [prices, pageConfig]);

  const regionPrices = useMemo(() => {
    if (pageConfig.isNational) return prices;
    return prices.filter(p => pageConfig.regionCodes.includes(p.regionCode));
  }, [prices, pageConfig]);

  return (
    <>
      <Helmet>
        <title>{pageConfig.metaTitle}</title>
        <meta name="description" content={pageConfig.metaDescription} />
        <link rel="canonical" href={`https://galonyilitry.pl${pageConfig.canonicalPath}`} />
      </Helmet>

      <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode} lang="pl">
        <div className="max-w-4xl mx-auto px-4 pt-20 pb-12">

          <h1 className={`text-3xl md:text-4xl font-extrabold mb-2 text-center ${
            darkMode ? 'text-white' : 'text-slate-900'
          }`}>
            {pageConfig.h1}
          </h1>

          <p className={`text-center text-sm mb-8 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Aktualizowane co tydzień · Źródło: EIA (U.S. Energy Information Administration)
          </p>

          <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
            {pageConfig.h2_1}
          </h2>

          <FuelPriceTable
            darkMode={darkMode}
            prices={regionPrices}
            dollarRate={dollarRate}
            lang="pl"
            isLoading={isLoading}
            lastUpdated={lastUpdated}
          />

          <AdPlaceholder darkMode={darkMode} />

          <SEOContent darkMode={darkMode} title={pageConfig.h2_2}>
            {typeof pageConfig.seoContent === 'function'
              ? pageConfig.seoContent(darkMode)
              : <p>{pageConfig.seoContent}</p>}
          </SEOContent>

        </div>
      </Layout>
    </>
  );
};

export default FuelPricePage;
